import {
	AttachmentType,
	AssociationStatus,
	AssociationType,
	LocationEventType,
	RateType,
	UserRole,
	WorkOrderPriority,
	WorkOrderStatus,
	type Association,
	type AssociationShortcutCode,
	type Attachment,
	type InspectionIssue,
	type InspectionReport,
	type InspectionReportIssue,
	type LocationEvent,
	type Material,
	type MaterialCost,
	type MiscCost,
	type Note,
	type TimeEntry,
	type User,
	type WorkOrder,
} from '@/types/models';

export type DemoSeedData = {
	users: User[];
	associations: Association[];
	associationShortcutCodes: AssociationShortcutCode[];
	workOrders: WorkOrder[];
	timeEntries: TimeEntry[];
	materials: Material[];
	materialCosts: MaterialCost[];
	miscCosts: MiscCost[];
	notes: Note[];
	attachments: Attachment[];
	locationEvents: LocationEvent[];
	inspectionReports: InspectionReport[];
	inspectionIssues: InspectionIssue[];
	inspectionReportIssues: InspectionReportIssue[];
};

const iso = (ms: number): string => new Date(ms).toISOString();

export const buildDemoSeedData = (demoUser: User): DemoSeedData => {
	const now = Date.now();
	const createdAt = iso(now - 1000 * 60 * 60 * 24); // 24h ago
	const updatedAt = iso(now - 1000 * 60 * 10); // 10m ago

	const association: Association = {
		id: 'demo-association-1',
		name: 'Demo Property Management',
		address: '123 Demo Ave',
		city: 'San Diego',
		state: 'CA',
		zipCode: '92101',
		zip: '92101',
		type: AssociationType.HOA,
		status: AssociationStatus.Active,
		lat: 32.7157,
		lng: -117.1611,
		latitude: 32.7157,
		longitude: -117.1611,
		contactName: 'Demo HOA Board',
		contactEmail: 'demo@example.com',
		contactPhone: '(555) 123-4567',
		notes: 'This is sample data for Apple review. Changes stay on device.',
		assignedTo: demoUser.id,
		numberOfUnits: 24,
		workOrderCount: 7,
		createdAt,
		updatedAt,
	};

	const workOrders: WorkOrder[] = [
		{
			id: 'demo-wo-1',
			orderNumber: 1001,
			workRequested: 'Fix leaking faucet in Unit 12B',
			jobLocation: 'Unit 12B - Kitchen',
			terms: 'Please coordinate access with tenant.',
			status: WorkOrderStatus.Open,
			priority: WorkOrderPriority.Urgent,
			dueDate: now + 1000 * 60 * 60 * 24, // tomorrow
			assignedTo: demoUser.id,
			associationId: association.id,
			createdBy: demoUser.id,
			ownerName: 'Tenant (Demo)',
			ownerPhone: '(555) 222-3333',
			ownerEmail: 'tenant.demo@example.com',
			latitude: 32.7157,
			longitude: -117.1611,
			createdAt,
			updatedAt,
		},
		{
			id: 'demo-wo-2',
			orderNumber: 1002,
			workRequested: 'Replace hallway light fixture (2nd floor)',
			jobLocation: 'Building A - 2nd floor hallway',
			terms: 'Use LED fixture if available.',
			status: WorkOrderStatus.InProgress,
			priority: WorkOrderPriority.High,
			assignedTo: demoUser.id,
			associationId: association.id,
			createdBy: demoUser.id,
			latitude: 32.716,
			longitude: -117.1614,
			createdAt: iso(now - 1000 * 60 * 60 * 12),
			updatedAt,
		},
		{
			id: 'demo-wo-3',
			orderNumber: 1003,
			workRequested: 'Re-key clubhouse door',
			workCompleted: 'Installed new lock and delivered keys to board.',
			jobLocation: 'Clubhouse',
			status: WorkOrderStatus.Completed,
			priority: WorkOrderPriority.Medium,
			assignedTo: demoUser.id,
			associationId: association.id,
			createdBy: demoUser.id,
			completedAt: iso(now - 1000 * 60 * 60 * 3),
			completionLat: 32.7158,
			completionLng: -117.1612,
			latitude: 32.7158,
			longitude: -117.1612,
			createdAt: iso(now - 1000 * 60 * 60 * 48),
			updatedAt: iso(now - 1000 * 60 * 60 * 3),
		},
		{
			id: 'demo-wo-4',
			orderNumber: 1004,
			workRequested: 'Paint touch-up in lobby',
			jobLocation: 'Main lobby',
			status: WorkOrderStatus.Cancelled,
			priority: WorkOrderPriority.Low,
			assignedTo: demoUser.id,
			associationId: association.id,
			createdBy: demoUser.id,
			createdAt: iso(now - 1000 * 60 * 60 * 72),
			updatedAt: iso(now - 1000 * 60 * 60 * 60),
		},
		{
			id: 'demo-wo-5',
			orderNumber: 1005,
			workRequested: 'Repaint exterior trim on buildings A & B',
			jobLocation: 'Buildings A and B — exterior',
			terms: 'Estimate pending HOA board approval before scheduling.',
			status: WorkOrderStatus.Quote,
			priority: WorkOrderPriority.Medium,
			dueDate: now + 1000 * 60 * 60 * 24 * 14,
			assignedTo: demoUser.id,
			associationId: association.id,
			createdBy: demoUser.id,
			ownerName: 'HOA Board',
			ownerPhone: '(555) 123-4567',
			latitude: 32.7159,
			longitude: -117.1613,
			createdAt: iso(now - 1000 * 60 * 60 * 8),
			updatedAt: iso(now - 1000 * 60 * 60 * 8),
		},
		{
			id: 'demo-wo-6',
			orderNumber: 1006,
			workRequested: 'Replace broken pool gate latch',
			jobLocation: 'Pool area entrance',
			terms: 'Waiting on commercial-grade latch kit from vendor.',
			status: WorkOrderStatus.PendingMaterials,
			priority: WorkOrderPriority.High,
			dueDate: now + 1000 * 60 * 60 * 24 * 3,
			assignedTo: demoUser.id,
			associationId: association.id,
			createdBy: demoUser.id,
			ownerName: 'Pool Committee',
			ownerPhone: '(555) 777-8888',
			latitude: 32.7161,
			longitude: -117.1615,
			createdAt: iso(now - 1000 * 60 * 60 * 18),
			updatedAt: iso(now - 1000 * 60 * 60 * 2),
		},
		{
			id: 'demo-wo-7',
			orderNumber: 1007,
			workRequested: 'Inspect and repair HVAC unit #3',
			jobLocation: 'Rooftop — Building C',
			terms: 'Scheduled with HVAC contractor, awaiting vendor arrival.',
			status: WorkOrderStatus.PendingVendor,
			priority: WorkOrderPriority.Medium,
			dueDate: now + 1000 * 60 * 60 * 24 * 5,
			assignedTo: demoUser.id,
			associationId: association.id,
			createdBy: demoUser.id,
			ownerName: 'HOA Maintenance',
			ownerPhone: '(555) 444-5555',
			latitude: 32.7156,
			longitude: -117.1609,
			createdAt: iso(now - 1000 * 60 * 60 * 30),
			updatedAt: iso(now - 1000 * 60 * 60 * 4),
		},
	];

	const notes: Note[] = [
		{
			id: 'demo-note-1',
			workOrderId: 'demo-wo-1',
			userId: demoUser.id,
			content: 'Customer reports steady drip, worse at night.',
			text: 'Customer reports steady drip, worse at night.',
			createdBy: demoUser.id,
			createdAtLat: 32.7157,
			createdAtLong: -117.1611,
			createdAt: iso(now - 1000 * 60 * 30),
			updatedAt: iso(now - 1000 * 60 * 30),
		},
		{
			id: 'demo-note-2',
			workOrderId: 'demo-wo-2',
			userId: demoUser.id,
			content: 'Fixture removed; waiting on replacement part.',
			text: 'Fixture removed; waiting on replacement part.',
			createdBy: demoUser.id,
			createdAtLat: 32.716,
			createdAtLong: -117.1614,
			createdAt: iso(now - 1000 * 60 * 90),
			updatedAt: iso(now - 1000 * 60 * 90),
		},
	];

	const timeEntries: TimeEntry[] = [
		{
			id: 'demo-time-1',
			workOrderId: 'demo-wo-2',
			userId: demoUser.id,
			startTime: iso(now - 1000 * 60 * 60 * 2),
			endTime: iso(now - 1000 * 60 * 60 * 1),
			description: 'Removed old fixture + inspected wiring',
			hourlyRate: 85,
			rateType: RateType.Standard,
			createdAt: iso(now - 1000 * 60 * 60 * 2),
			updatedAt: iso(now - 1000 * 60 * 60 * 1),
		},
	];

	const materials: Material[] = [
		{
			id: 'demo-material-1',
			name: 'LED Bulb (Warm White)',
			description: '60W equivalent',
			unit: 'each',
			costPerUnit: 4.5,
			workOrderId: 'demo-wo-2',
			createdAt: iso(now - 1000 * 60 * 60 * 2),
			updatedAt: iso(now - 1000 * 60 * 60 * 2),
		},
	];

	const materialCosts: MaterialCost[] = [
		{
			id: 'demo-material-cost-1',
			workOrderId: 'demo-wo-2',
			materialId: 'demo-material-1',
			quantity: 4,
			costPerUnit: 4.5,
			totalCost: 18,
			description: 'LED bulbs',
			name: 'LED Bulb (Warm White)',
			unitType: 'each',
			createdAt: iso(now - 1000 * 60 * 60 * 2),
			updatedAt: iso(now - 1000 * 60 * 60 * 2),
		},
	];

	const miscCosts: MiscCost[] = [
		{
			id: 'demo-misc-cost-1',
			workOrderId: 'demo-wo-3',
			description: 'Lock re-key service',
			amount: 65,
			quantity: 1,
			unitType: 'service',
			totalCost: 65,
			createdAt: iso(now - 1000 * 60 * 60 * 5),
			updatedAt: iso(now - 1000 * 60 * 60 * 5),
		},
	];

	const attachments: Attachment[] = [
		{
			id: 'demo-attachment-1',
			workOrderId: 'demo-wo-2',
			fileName: 'before.jpg',
			fileSize: 245_000,
			fileType: 'jpg',
			attachmentType: AttachmentType.BeforePhoto,
			photoTakenAt: iso(now - 1000 * 60 * 60 * 6),
			photoLat: 32.716,
			photoLng: -117.1614,
			isImage: true,
			mimeType: 'image/jpeg',
			createdAt: iso(now - 1000 * 60 * 60 * 6),
			updatedAt: iso(now - 1000 * 60 * 60 * 6),
		},
	];

	const locationEvents: LocationEvent[] = [
		{
			id: 'demo-loc-1',
			userId: demoUser.id,
			associationId: association.id,
			type: LocationEventType.ManualCheckIn,
			lat: 32.7157,
			lng: -117.1611,
			accuracy: 12,
			timestamp: iso(now - 1000 * 60 * 45),
			createdAt: iso(now - 1000 * 60 * 45),
			updatedAt: iso(now - 1000 * 60 * 45),
		},
		{
			id: 'demo-loc-2',
			userId: demoUser.id,
			associationId: association.id,
			type: LocationEventType.ManualCheckOut,
			lat: 32.7157,
			lng: -117.1611,
			accuracy: 12,
			timestamp: iso(now - 1000 * 60 * 15),
			createdAt: iso(now - 1000 * 60 * 15),
			updatedAt: iso(now - 1000 * 60 * 15),
		},
	];

	const associationShortcutCodes: AssociationShortcutCode[] = [
		{
			id: 'demo-shortcut-1',
			associationId: association.id,
			shortcutCode: '1234',
			fullAddress: `${association.address}, ${association.city}, ${association.state} ${association.zipCode}`,
			city: association.city,
			state: association.state,
			zipCode: association.zipCode,
			zip: association.zipCode,
			latitude: association.latitude,
			longitude: association.longitude,
			lat: association.latitude,
			lng: association.longitude,
			createdBy: demoUser.id,
			createdAt,
			updatedAt,
		},
	];

	// Minimal inspection seed (feature can still be demoed)
	const inspectionReports: InspectionReport[] = [
		{
			id: 'demo-inspection-1',
			associationId: association.id,
			userId: demoUser.id,
			title: 'Monthly Walkthrough (Demo)',
			description: 'Sample inspection report for demo mode',
			scheduledDate: iso(now - 1000 * 60 * 60 * 24 * 7),
			reportNumber: 'D-1001',
			createdAt: iso(now - 1000 * 60 * 60 * 24 * 7),
			updatedAt: iso(now - 1000 * 60 * 60 * 24 * 7),
		},
	];

	const inspectionIssues: InspectionIssue[] = [
		{
			id: 'demo-issue-1',
			title: 'Cracked stair tile',
			description: 'Replace damaged tile on 2nd floor stairs',
			severity: 'medium',
			category: 'safety',
			name: 'Cracked stair tile',
			createdAt: iso(now - 1000 * 60 * 60 * 24 * 14),
			updatedAt: iso(now - 1000 * 60 * 60 * 24 * 14),
		},
	];

	const inspectionReportIssues: InspectionReportIssue[] = [
		{
			id: 'demo-report-issue-1',
			inspectionReportId: 'demo-inspection-1',
			inspectionIssueId: 'demo-issue-1',
			notes: 'Marked for follow-up next week.',
			resolved: false,
			createdAt: iso(now - 1000 * 60 * 60 * 24 * 7),
			updatedAt: iso(now - 1000 * 60 * 60 * 24 * 7),
		},
	];

	const seededDemoUser: User = {
		...demoUser,
		role: UserRole.Admin,
		isAdmin: true,
		organizationId: 'demo',
		createdAt: demoUser.createdAt || createdAt,
		updatedAt: demoUser.updatedAt || updatedAt,
	};

	return {
		users: [seededDemoUser],
		associations: [association],
		associationShortcutCodes,
		workOrders,
		timeEntries,
		materials,
		materialCosts,
		miscCosts,
		notes,
		attachments,
		locationEvents,
		inspectionReports,
		inspectionIssues,
		inspectionReportIssues,
	};
};

